import {
  Add,
  ChangeCircle,
  Download,
  Edit as EditIcon,
  Error as ErrorIcon,
  Lock as LockIcon,
  Send,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

// ---------------------------------------------------------------------------
// Shared style constants
// ---------------------------------------------------------------------------

const statusBarSx = {
  mx: -2,
  mt: 1,
  px: 2,
  py: 0.75,
  minHeight: 48,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
} as const;

const cutsheetActionButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  px: 2,
} as const;

const mobileIconButtonSx = {
  color: 'primary.main',
  border: 1,
  borderColor: 'primary.main',
  width: 44,
  height: 44,
} as const;

// ---------------------------------------------------------------------------
// Status bars
// ---------------------------------------------------------------------------

export function WarningBar({
  reminderSentTo,
}: {
  reminderSentTo?: string | null;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        ...statusBarSx,
        bgcolor: alpha(
          theme.palette.warning.main,
          theme.palette.mode === 'dark' ? 0.2 : 0.12,
        ),
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.5}>
        <WarningIcon sx={{ fontSize: 18, color: 'warning.dark' }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: 'warning.dark' }}
        >
          Request sent — buyer hasn&apos;t responded yet
        </Typography>
      </Stack>
      {reminderSentTo && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 3.25 }}>
          Sent to {reminderSentTo}
        </Typography>
      )}
    </Box>
  );
}

export function LockedBar({
  processorName,
  cutsheetName,
}: {
  processorName?: string;
  cutsheetName?: string;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        ...statusBarSx,
        bgcolor: alpha(
          theme.palette.warning.main,
          theme.palette.mode === 'dark' ? 0.2 : 0.12,
        ),
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.5}>
        <LockIcon sx={{ fontSize: 18, color: 'warning.dark' }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: 'warning.dark' }}
        >
          Locked by {processorName || 'the processor'}
        </Typography>
      </Stack>
      {cutsheetName && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 3.25 }}>
          Cutsheet: {cutsheetName}
        </Typography>
      )}
    </Box>
  );
}

export function ErrorBar({ message }: { message: string }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        ...statusBarSx,
        bgcolor: alpha(
          theme.palette.error.main,
          theme.palette.mode === 'dark' ? 0.2 : 0.12,
        ),
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.5}>
        <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: 'error.main' }}
        >
          {message}
        </Typography>
      </Stack>
    </Box>
  );
}

export function SuccessBar({
  cutsheetName,
  isResponse = false,
  onPreview,
}: {
  cutsheetName: string;
  isResponse?: boolean;
  onPreview?: () => void;
}) {
  const theme = useTheme();
  const message = isResponse
    ? `${cutsheetName} responses added`
    : `Cutsheet selected: ${cutsheetName}`;
  const previewTitle = isResponse ? 'View responses' : 'Preview cutsheet';
  return (
    <Box
      sx={{
        ...statusBarSx,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: alpha(
          theme.palette.success.main,
          theme.palette.mode === 'dark' ? 0.2 : 0.12,
        ),
      }}
    >
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: 'success.dark' }}
      >
        {message}
      </Typography>
      {onPreview && (
        <Tooltip title={previewTitle}>
          <IconButton
            size="small"
            onClick={onPreview}
            sx={{ color: 'success.dark' }}
            aria-label={previewTitle}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Cutsheet action buttons
// ---------------------------------------------------------------------------

export function LockedCutsheetActions({
  downloadingPdf,
  onDownloadPdf,
}: {
  downloadingPdf: boolean;
  onDownloadPdf: () => void;
}) {
  return (
    <Stack
      direction="column"
      alignItems="flex-start"
      gap={1}
      sx={{ width: '100%' }}
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={
          downloadingPdf ? <CircularProgress size={16} /> : <Download />
        }
        disabled={downloadingPdf}
        onClick={onDownloadPdf}
        sx={{
          ...cutsheetActionButtonSx,
          display: { xs: 'none', sm: 'inline-flex' },
        }}
      >
        {downloadingPdf ? 'Working...' : 'Download'}
      </Button>
      <Stack
        alignItems="center"
        gap={0.25}
        onClick={downloadingPdf ? undefined : onDownloadPdf}
        sx={{
          cursor: downloadingPdf ? 'default' : 'pointer',
          display: { xs: 'flex', sm: 'none' },
        }}
      >
        <IconButton disabled={downloadingPdf} sx={mobileIconButtonSx}>
          {downloadingPdf ? <CircularProgress size={20} /> : <Download />}
        </IconButton>
        <Typography variant="caption" sx={{ color: 'primary.main' }}>
          {downloadingPdf ? 'Working...' : 'Download'}
        </Typography>
      </Stack>
    </Stack>
  );
}

export function UnlockedCutsheetActions({
  downloadingPdf,
  onFillCutsheet,
  onEditCutsheet,
  onDownloadPdf,
}: {
  downloadingPdf: boolean;
  onFillCutsheet: () => void;
  onEditCutsheet: () => void;
  onDownloadPdf: () => void;
}) {
  return (
    <Stack
      direction="column"
      alignItems="flex-start"
      gap={1}
      sx={{ width: '100%' }}
    >
      <Stack
        direction="row"
        gap={1}
        sx={{ display: { xs: 'none', sm: 'flex' }, width: '100%' }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<ChangeCircle />}
          onClick={onFillCutsheet}
          sx={{ ...cutsheetActionButtonSx, flex: 1 }}
        >
          Change
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
          onClick={onEditCutsheet}
          sx={{ ...cutsheetActionButtonSx, flex: 1 }}
        >
          Edit
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={
            downloadingPdf ? <CircularProgress size={16} /> : <Download />
          }
          disabled={downloadingPdf}
          onClick={onDownloadPdf}
          sx={{ ...cutsheetActionButtonSx, flex: 1 }}
        >
          {downloadingPdf ? 'Working...' : 'Download'}
        </Button>
      </Stack>
      <Stack
        direction="row"
        gap={1.5}
        sx={{ display: { xs: 'flex', sm: 'none' } }}
      >
        <Stack
          alignItems="center"
          gap={0.25}
          onClick={onFillCutsheet}
          sx={{ cursor: 'pointer' }}
        >
          <IconButton sx={mobileIconButtonSx}>
            <ChangeCircle />
          </IconButton>
          <Typography variant="caption" sx={{ color: 'primary.main' }}>
            Change
          </Typography>
        </Stack>
        <Stack
          alignItems="center"
          gap={0.25}
          onClick={onEditCutsheet}
          sx={{ cursor: 'pointer' }}
        >
          <IconButton sx={mobileIconButtonSx}>
            <EditIcon />
          </IconButton>
          <Typography variant="caption" sx={{ color: 'primary.main' }}>
            Edit
          </Typography>
        </Stack>
        <Stack
          alignItems="center"
          gap={0.25}
          onClick={downloadingPdf ? undefined : onDownloadPdf}
          sx={{ cursor: downloadingPdf ? 'default' : 'pointer' }}
        >
          <IconButton disabled={downloadingPdf} sx={mobileIconButtonSx}>
            {downloadingPdf ? <CircularProgress size={20} /> : <Download />}
          </IconButton>
          <Typography variant="caption" sx={{ color: 'primary.main' }}>
            {downloadingPdf ? 'Working...' : 'Download'}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}

export function NoCutsheetActions({
  onFillCutsheet,
}: {
  onFillCutsheet: () => void;
}) {
  return (
    <Stack direction="row" gap={1}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Add />}
        onClick={onFillCutsheet}
        sx={cutsheetActionButtonSx}
      >
        Fill in cutsheet
      </Button>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Producer-only action buttons (reminder / fill-in)
// ---------------------------------------------------------------------------

export function ReminderActions({
  sendingReminder,
  showDone,
  resendIcon,
  onSendReminder,
  onFillCutsheet,
}: {
  sendingReminder: boolean;
  showDone: boolean;
  resendIcon: React.ReactNode;
  onSendReminder: () => void;
  onFillCutsheet: () => void;
}) {
  return (
    <Stack direction="row" gap={1} alignItems="center">
      <Button
        variant="outlined"
        size="small"
        startIcon={resendIcon}
        disabled={sendingReminder || showDone}
        onClick={onSendReminder}
        sx={{ textTransform: 'none' }}
      >
        {showDone ? 'Done' : 'Resend request'}
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={onFillCutsheet}
        sx={{ textTransform: 'none' }}
      >
        Fill in myself
      </Button>
    </Stack>
  );
}

export function MissingCutsheetActions({
  isSelf,
  canSendReminder,
  sendingReminder,
  onFillCutsheet,
  onSendReminder,
}: {
  isSelf: boolean;
  canSendReminder: boolean;
  sendingReminder: boolean;
  onFillCutsheet: () => void;
  onSendReminder: () => void;
}) {
  return (
    <Stack direction="row" gap={1} alignItems="center">
      <Button
        variant="outlined"
        size="small"
        startIcon={<Add />}
        onClick={onFillCutsheet}
        sx={{ textTransform: 'none' }}
      >
        Fill in cutsheet
      </Button>
      {!isSelf && canSendReminder && (
        <Button
          variant="outlined"
          size="small"
          startIcon={
            sendingReminder ? <CircularProgress size={16} /> : <Send />
          }
          disabled={sendingReminder}
          onClick={onSendReminder}
          sx={{ textTransform: 'none' }}
        >
          Send request
        </Button>
      )}
    </Stack>
  );
}
